import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {}; // module safety

const BASE_URL = "/api";

type RepresentativeData = {
  merchant_id?: string;

  rep_name: string;
  rep_name_kana: string;
  birth_date: string;
  gender: string;
  spouse: string;

  home_postal_code: string;
  home_city: string;
  home_address_rest: string;

  housing_type: string;
  residence_years: number | null;

  rep_phone_landline: string;
  rep_phone_mobile: string;
  rep_email: string;

  career: string;

  is_completed: boolean;
  updated_at?: string;
};

function ReadonlyRow({ label, value }: { label: string; value: any }) {
  const text = value === null || value === undefined ? "" : String(value);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <div
        style={{
          fontWeight: 500,
          color: "#334155",
          whiteSpace: "pre-wrap",
          lineHeight: 1.6,
        }}
      >
        {text.trim() ? text : <span style={{ color: "#94a3b8" }}>—</span>}
      </div>
    </div>
  );
}

function EditRow({
  label,
  value,
  onChange,
  placeholder,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        type={type || "text"}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          fontWeight: 700,
          color: "#0f172a",
          outline: "none",
          background: "#fff",
        }}
      />
    </div>
  );
}

function EditSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          fontWeight: 700,
          color: "#0f172a",
          outline: "none",
          background: "#fff",
        }}
      >
        <option value="">選択してください</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}







function ReadonlyInputBox({
  label,
  value,
  required,
  full,
}: {
  label: string;
  value: any;
  required?: boolean;
  full?: boolean;
}) {
  const text = value === null || value === undefined ? "" : String(value);
  const displayValue = text.trim() ? text : "—";

  return (
    <div style={{ minWidth: 0, gridColumn: full ? "1 / -1" : undefined }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          width: "100%",
          minHeight: 44,
          padding: "0 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          fontWeight: 700,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          whiteSpace: full ? "pre-wrap" : "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: 1.6,
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

export default function StaffFormViewRepresentative() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RepresentativeData | null>(null);
  const [error, setError] = useState("");

  const [downloading, setDownloading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const [draft, setDraft] = useState({
    rep_name: "",
    rep_name_kana: "",
    birth_date: "",
    gender: "",
    spouse: "",
    home_postal_code: "",
    home_city: "",
    home_address_rest: "",
    housing_type: "",
    residence_years: "",
    rep_phone_landline: "",
    rep_phone_mobile: "",
    rep_email: "",
    career: "",
  });

  const load = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");
    setSaveMsg("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form2_representative`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d: RepresentativeData | null = json?.data || null;
      setData(d);

      setDraft({
        rep_name: d?.rep_name || "",
        rep_name_kana: d?.rep_name_kana || "",
        birth_date: d?.birth_date || "",
        gender: d?.gender || "",
        spouse: d?.spouse || "",
        home_postal_code: d?.home_postal_code || "",
        home_city: d?.home_city || "",
        home_address_rest: d?.home_address_rest || "",
        housing_type: d?.housing_type || "",
        residence_years: d?.residence_years === null || d?.residence_years === undefined ? "" : String(d.residence_years),
        rep_phone_landline: d?.rep_phone_landline || "",
        rep_phone_mobile: d?.rep_phone_mobile || "",
        rep_email: d?.rep_email || "",
        career: d?.career || "",
      });

      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム②（代表者情報）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム2_代表者情報_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const startEdit = () => {
    setSaveMsg("");
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraft({
      rep_name: data?.rep_name || "",
      rep_name_kana: data?.rep_name_kana || "",
      birth_date: data?.birth_date || "",
      gender: data?.gender || "",
      spouse: data?.spouse || "",
      home_postal_code: data?.home_postal_code || "",
      home_city: data?.home_city || "",
      home_address_rest: data?.home_address_rest || "",
      housing_type: data?.housing_type || "",
      residence_years:
        data?.residence_years === null || data?.residence_years === undefined ? "" : String(data.residence_years),
      rep_phone_landline: data?.rep_phone_landline || "",
      rep_phone_mobile: data?.rep_phone_mobile || "",
      rep_email: data?.rep_email || "",
      career: data?.career || "",
    });
    setIsEditing(false);
    setSaveMsg("");
  };

  const saveEdit = async () => {
    if (!merchantId) return;

    try {
      setSaving(true);
      setSaveMsg("");

      const body = {
        rep_name: draft.rep_name,
        rep_name_kana: draft.rep_name_kana,
        birth_date: draft.birth_date,
        gender: draft.gender,
        spouse: draft.spouse,

        home_postal_code: draft.home_postal_code,
        home_city: draft.home_city,
        home_address_rest: draft.home_address_rest,

        housing_type: draft.housing_type,
        residence_years: draft.residence_years.trim() ? Number(draft.residence_years) : null,

        rep_phone_landline: draft.rep_phone_landline,
        rep_phone_mobile: draft.rep_phone_mobile,
        rep_email: draft.rep_email,

        career: draft.career,
      };

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form2_representative`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "保存に失敗しました。");

      setSaveMsg("保存しました。");
      setIsEditing(false);
      await load();
    } catch (e: any) {
      setSaveMsg(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffLayout title="フォーム② 代表者情報（確認）" active="progress">
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        {!isEditing ? (
          <button
            onClick={startEdit}
            disabled={loading || !!error || !data}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #0f172a",
              background: "#fff",
              color: "#0f172a",
              fontWeight: 900,
              cursor: "pointer",
              opacity: loading || !!error || !data ? 0.6 : 1,
            }}
            title={!data ? "データが存在しないため編集できません" : ""}
          >
            編集
          </button>
        ) : (
          <>
            <button
              onClick={saveEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #16a34a",
                background: "#16a34a",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
              onClick={cancelEdit}
              disabled={saving}
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.8 : 1,
              }}
            >
              キャンセル
            </button>
          </>
        )}
      </div>

      {saveMsg && (
        <div
          style={{
            marginBottom: 10,
            background: saveMsg.includes("失敗") ? "#fff1f2" : "#ecfdf5",
            border: saveMsg.includes("失敗") ? "1px solid #fecdd3" : "1px solid #bbf7d0",
            color: saveMsg.includes("失敗") ? "#b91c1c" : "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {saveMsg}
        </div>
      )}

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data?.is_completed ? "#166534" : "#9a3412" }}>
              {data?.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data?.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
            {isEditing && (
              <div style={{ marginTop: 8, color: "#0f172a", fontWeight: 900 }}>
                ※ スタッフ編集モード
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              {isEditing ? "入力内容（編集）" : "入力内容（読み取り専用）"}
            </div>

            {isEditing ? (
              <>
                <EditRow label="代表者名" value={draft.rep_name} onChange={(v) => setDraft((p) => ({ ...p, rep_name: v }))} />
                <EditRow
                  label="代表者名フリガナ"
                  value={draft.rep_name_kana}
                  onChange={(v) => setDraft((p) => ({ ...p, rep_name_kana: v }))}
                />
                <EditRow
                  label="生年月日"
                  value={draft.birth_date}
                  onChange={(v) => setDraft((p) => ({ ...p, birth_date: v }))}
                  placeholder="例）2025-12-11"
                />
                <EditSelect
                  label="性別"
                  value={draft.gender}
                  onChange={(v) => setDraft((p) => ({ ...p, gender: v }))}
                  options={["男", "女", "その他"]}
                />
                <EditSelect
                  label="配偶者有無"
                  value={draft.spouse}
                  onChange={(v) => setDraft((p) => ({ ...p, spouse: v }))}
                  options={["有", "無"]}
                />

                <EditRow
                  label="自宅住所 郵便番号"
                  value={draft.home_postal_code}
                  onChange={(v) => setDraft((p) => ({ ...p, home_postal_code: v }))}
                />
                <EditRow
                  label="自宅住所 ～市町村丁目"
                  value={draft.home_city}
                  onChange={(v) => setDraft((p) => ({ ...p, home_city: v }))}
                />
                <EditRow
                  label="自宅住所 番地以降"
                  value={draft.home_address_rest}
                  onChange={(v) => setDraft((p) => ({ ...p, home_address_rest: v }))}
                />

                <EditRow
                  label="住居形態"
                  value={draft.housing_type}
                  onChange={(v) => setDraft((p) => ({ ...p, housing_type: v }))}
                />
                <EditRow
                  label="居住年数"
                  value={draft.residence_years}
                  onChange={(v) => setDraft((p) => ({ ...p, residence_years: v }))}
                  placeholder="例）5"
                  type="number"
                />

                <EditRow
                  label="代表者固定電話"
                  value={draft.rep_phone_landline}
                  onChange={(v) => setDraft((p) => ({ ...p, rep_phone_landline: v }))}
                />
                <EditRow
                  label="代表者携帯"
                  value={draft.rep_phone_mobile}
                  onChange={(v) => setDraft((p) => ({ ...p, rep_phone_mobile: v }))}
                />

                <EditRow
  label="代表者メールアドレス"
  value={draft.rep_email}
  onChange={(v) => setDraft((p) => ({ ...p, rep_email: v }))}
/>

                <EditRow
                  label="経歴"
                  value={draft.career}
                  onChange={(v) => setDraft((p) => ({ ...p, career: v }))}
                />
              </>
            ) : (
  <div
    style={{
      padding: 12,
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
      gap: 20,
      alignItems: "start",
    }}
  >
    <ReadonlyInputBox label="代表者名（全角）" value={data?.rep_name} required />
    <ReadonlyInputBox label="代表者名フリガナ（半角）" value={data?.rep_name_kana} required />

    <ReadonlyInputBox label="メールアドレス" value={data?.rep_email} />
    <ReadonlyInputBox label="生年月日" value={data?.birth_date} required />

    <ReadonlyInputBox label="性別" value={data?.gender} required />
    <ReadonlyInputBox label="配偶者有無" value={data?.spouse} required />

    <ReadonlyInputBox label="自宅住所 郵便番号" value={data?.home_postal_code} required />
    <ReadonlyInputBox label="自宅住所（都道府県・市区町村・全角）" value={data?.home_city} required />

    <ReadonlyInputBox label="自宅住所（番地以降・全角）" value={data?.home_address_rest} required />
    <ReadonlyInputBox label="住居形態" value={data?.housing_type} />

    <ReadonlyInputBox label="居住年数" value={data?.residence_years ? `${data.residence_years}年` : ""} />
    <ReadonlyInputBox label="代表者固定電話" value={data?.rep_phone_landline} />

    <ReadonlyInputBox label="代表者携帯" value={data?.rep_phone_mobile} required />
    <ReadonlyInputBox label="経歴" value={data?.career} required full />
  </div>
)}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
